import { useRef, useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Loader2,
  Eye,
  Code,
  ImagePlus,
  Palette,
  Images,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";
import { useSlideStore } from "@/stores/slide-store";
import { TEMPLATE_ID } from "@/lib/templates";
import { ViewportSize, VIEWPORT_SIZES, type ViewportSizeType } from "@/types/viewport-device.types";
import { setViewportApplier } from "@/lib/socket/agentEventHandlers";
import type { SetViewportPayload } from "@optidev/shared-types";

import IntegratedEditorWithAgent from "@/components/editor/IntegratedEditorWithAgent";
import { EditorTour } from "@/components/onboarding";
import { useVisualEditorHistoryStore } from "@/stores/visualEditorHistoryStore";
import { getAllPendingChanges } from "@/lib/utils/visual-editor-history-utils";
import { useDevContainerHeartbeat } from "@/hooks/useDevContainerHeartbeat";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import { UnsavedChangesDialog } from "@/components/visual-editor/unsaved-changes-dialog";
import { POWER_EDITOR_STYLES } from "@/constants/power-editor.constants";
import type { ViewMode } from "@/types/power-editor.types";
import { usePowerEditorState } from "@/hooks/usePowerEditorState";
import { usePowerEditorActions } from "@/hooks/usePowerEditorActions";
import PowerEditorHeader from "@/components/editor/power/PowerEditorHeader";
import PowerEditorStatusPage from "@/components/editor/power/PowerEditorStatusPage";
import { useRecoveryNoticeStore } from "@/stores/recovery-notice-store";
import PowerEditorFloatingControls from "@/components/editor/power/PowerEditorFloatingControls";
import { RenameProjectModal } from "@/components/editor/RenameProjectModal";
import { useResponsive } from "@/hooks/useResponsive";
import { useAuth } from "@/contexts/AuthContext";
import { projectsApi } from "@/lib/api/projects";
import { useFeatures } from "@/hooks/useFeatures";
import type { Connection } from "@optidev/shared-types";

export default function PowerEditorFull() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = params.projectId as string;
  const { getIdToken } = useAuth();
  const { features } = useFeatures();

  // Rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);

  // Unsaved changes guard for path/route navigation and view mode changes
  const [pendingPathRoute, setPendingPathRoute] = useState<string | null>(null);
  const [pendingViewMode, setPendingViewMode] = useState<ViewMode | null>(null);

  // Active menu item state (for dropdown menu in header)
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null);

  // Agent sendPrompt ref - exposed from IntegratedEditorWithAgent
  const agentSendPromptRef = useRef<((text: string) => void) | null>(null);

  // Mobile settings opener ref - exposed from IntegratedEditorWithAgent
  const mobileSettingsOpenerRef = useRef<((section?: 'info' | 'deploy' | 'optidev-cloud' | 'agent-settings') => void) | null>(null);

  // Custom hooks for state and actions
  const state = usePowerEditorState();

  // Detect slide project from builder tags, reset on project change
  const slideReset = useSlideStore((s) => s.reset);
  const setIsSlideProject = useSlideStore((s) => s.setIsSlideProject);
  useEffect(() => {
    // Reset slide state when entering a new project
    slideReset();
    // Optimistic slide-project hint dựa trên TEMPLATE THỰC TẾ (templateId),
    // KHÔNG dựa trên builderData.tags. Tags do LLM phân loại nên một project
    // base vẫn có thể bị gán 'presentation' (vd ["landing-page","presentation"])
    // → trước đây .includes() làm Slides panel hiện nhầm. templateId phản ánh
    // đúng template đã scaffold (kể cả với project clone / project cũ).
    // Chỉ tin khi project đã load khớp route. fetchSlideManifest (slides.json)
    // vẫn là nguồn đính chính cuối cùng sau khi container sẵn sàng.
    if (
      state.project?.id === projectId &&
      state.project?.templateId === TEMPLATE_ID.SLIDES
    ) {
      setIsSlideProject(true);
    }
  }, [
    projectId,
    state.project?.id,
    state.project?.templateId,
    setIsSlideProject,
    slideReset,
  ]);

  const visualEditorRef = useRef<any>(null);

  const actions = usePowerEditorActions({
    projectId,
    project: state.project,
    workspace: state.workspace,
    previewIframeRef: state.previewIframeRef,
    visualEditorRef,
    pollingCleanupRef: state.pollingCleanupRef,
    previewIframeForPollingRef: state.previewIframeForPollingRef,
    computedActualPreviewUrl: state.computedActualPreviewUrl,
    showPreview: state.showPreview,
    isDevelopment: state.isDevelopment,

    // Setters
    setProject: state.setProject,
    setCurrentProject: state.setCurrentProject,
    setWorkspace: state.setWorkspace,
    setLoading: state.setLoading,
    setError: state.setError,
    setAccessUrl: state.setAccessUrl,
    setPreviewUrl: state.setPreviewUrl,
    setIsResuming: state.setIsResuming,
    setIsWorkspaceUnreachable: state.setIsWorkspaceUnreachable,
    setIsPreviewLoading: state.setIsPreviewLoading,
    setIsShowingStaticPreview: state.setIsShowingStaticPreview,
    setAppService: state.setAppService,
    setRetryCount: state.setRetryCount,
    setViewMode: state.setViewMode,
    setIsSidebarHidden: state.setIsSidebarHidden,
    setShowPreview: state.setShowPreview,
    setIsPreviewDisabled: state.setIsPreviewDisabled,
    setIsSavingChanges: state.setIsSavingChanges,
    setCommitState: state.setCommitState,
    setIsResponsiveMode: state.setIsResponsiveMode,
    setViewportSize: state.setViewportSize,
    setIsCustomViewport: state.setIsCustomViewport,
    setCustomWidth: state.setCustomWidth,
    setCustomHeight: state.setCustomHeight,
    setCurrentPath: state.setCurrentPath,
    setShowDeviceDropdown: state.setShowDeviceDropdown,
    setShowPathDropdown: state.setShowPathDropdown,
  });

  // ---- Agent-driven preview viewport (signage/kiosk) ----------------------
  // Mirrors exactly what the manual signage/custom dropdown selection does:
  // responsive mode on + custom dimensions drive the iframe size (see
  // IntegratedEditorWithAgent: isCustomViewport → width/height).
  const {
    setIsResponsiveMode,
    setIsCustomViewport,
    setCustomWidth,
    setCustomHeight,
    setViewportSize,
  } = state;

  const applyViewport = useCallback(
    (pv: { type: SetViewportPayload["type"]; width: number; height: number }) => {
      // "Full Size" = responsive mode off, the preview fills the whole pane
      // (no fixed canvas). Width/height are meaningless here so we ignore them.
      if (pv.type === "full") {
        setIsResponsiveMode(false);
        setIsCustomViewport(false);
        return;
      }
      setIsResponsiveMode(true);
      setIsCustomViewport(true);
      // For known device presets, ignore the stored width/height and use the
      // current preset constants — so projects assigned an older Desktop/Mobile
      // canvas pick up the latest dimensions automatically. `custom` keeps the
      // stored dims (user-defined, not tied to a preset).
      const presetByType: Partial<Record<SetViewportPayload["type"], ViewportSizeType>> = {
        mobile: ViewportSize.mobile,
        tablet: ViewportSize.tablet,
        desktop: ViewportSize.desktop,
        landscape: ViewportSize.signageLandscape,
        portrait: ViewportSize.signagePortrait,
      };
      const preset = presetByType[pv.type];
      const dims = preset ? VIEWPORT_SIZES[preset] : { width: pv.width, height: pv.height };
      setCustomWidth(dims.width as number);
      setCustomHeight(dims.height as number);
      if (preset) setViewportSize(preset);
    },
    [
      setIsResponsiveMode,
      setIsCustomViewport,
      setCustomWidth,
      setCustomHeight,
      setViewportSize,
    ],
  );

  // Register the applier so the agent's SET_VIEWPORT socket event can drive the
  // preview while the editor is mounted (with a user-visible toast).
  const applyAgentViewport = useCallback(
    (payload: SetViewportPayload) => {
      applyViewport(payload);
      const labels: Record<SetViewportPayload["type"], string> = {
        mobile: "Mobile",
        tablet: "Tablet",
        desktop: "Desktop",
        landscape: "Signage Landscape",
        portrait: "Signage Portrait",
        custom: "Custom size",
        full: "Full Size",
      };
      toast.success(
        payload.type === "full"
          ? "Preview switched to Full Size (responsive)"
          : `Preview switched to ${labels[payload.type]} (${payload.width}×${payload.height})`,
      );
    },
    [applyViewport],
  );

  useEffect(() => {
    setViewportApplier(applyAgentViewport);
    return () => setViewportApplier(null);
  }, [applyAgentViewport]);

  // Restore the persisted viewport once per project load (no toast) so the
  // editor opens on the correct canvas instead of Full Size.
  const restoredViewportProjectRef = useRef<string | null>(null);
  useEffect(() => {
    const pv = state.project?.previewViewport;
    const pid = state.project?.id;
    if (!pv || !pid || restoredViewportProjectRef.current === pid) return;
    restoredViewportProjectRef.current = pid;
    applyViewport(pv);
  }, [state.project, applyViewport]);

  // Publish the crash-recovery notice (if any) to the dashboard layout's
  // attention area, where WorkspaceRecoveryBanner renders alongside the
  // Maintenance/Announcement/Billing banners. Cleared when leaving the project.
  const setRecoveryNotice = useRecoveryNoticeStore((s) => s.setNotice);
  const clearRecoveryNotice = useRecoveryNoticeStore((s) => s.clear);
  useEffect(() => {
    const notice = state.workspace?.recoveryNotice;
    if (notice && projectId) {
      setRecoveryNotice(projectId, notice);
    }
    return () => clearRecoveryNotice();
  }, [state.workspace?.recoveryNotice, projectId, setRecoveryNotice, clearRecoveryNotice]);

  // Send periodic heartbeats to keep dev container active while user has project open
  // Also detects when browser wakes from sleep and auto-resumes if container was paused
  useDevContainerHeartbeat({
    devContainerId: state.workspace?.id,
    onContainerPaused: actions.handleContainerPausedDuringSleep,
    onContainerUnhealthy: actions.handleContainerUnhealthy,
  });

  // Get visual editor history state
  const history = useVisualEditorHistoryStore((state) => state.history);
  const currentIndex = useVisualEditorHistoryStore(
    (state) => state.currentIndex,
  );
  const canUndoFn = useVisualEditorHistoryStore((state) => state.canUndo);
  const canRedoFn = useVisualEditorHistoryStore((state) => state.canRedo);
  const canUndoValue = canUndoFn();
  const canRedoValue = canRedoFn();

  // Guarded path select — checks for unsaved visual editor changes before navigating
  const handlePathSelectWithGuard = useCallback(
    (path: string) => {
      if (history.length > 0) {
        setPendingPathRoute(path);
        return;
      }
      actions.handlePathSelect(path);
    },
    [history.length, actions.handlePathSelect],
  );

  // Guarded view mode change — checks for unsaved changes when leaving visual mode
  const handleViewModeChangeWithGuard = useCallback(
    (mode: ViewMode) => {
      if (state.viewMode === "visual" && mode !== "visual" && history.length > 0) {
        setPendingViewMode(mode);
        return;
      }
      state.setViewMode(mode);
    },
    [state.viewMode, history.length, state.setViewMode],
  );
  const { isMobile } = useResponsive();
  useEffect(() => {
    actions.loadProjectAndWorkspace();
  }, [projectId, actions.loadProjectAndWorkspace]);

  // Fetch available routes when workspace becomes running
  useEffect(() => {
    if (state.workspace?.status === "running") {
      actions.fetchRoutes();
    }
  }, [state.workspace?.status, actions.fetchRoutes]);

  // Sync viewMode state to URL params
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (state.viewMode !== "settings" && currentTab === "settings") {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("tab");
      newParams.delete("section");
      setSearchParams(newParams);
    }
  }, [state.viewMode, searchParams, setSearchParams]);

  const handleSettingsNavigation = useCallback(
    (section: string) => {
      const targetMode: ViewMode = section ? "settings" : "agent";

      // Check unsaved changes guard when leaving visual mode
      if (state.viewMode === "visual" && history.length > 0) {
        setPendingViewMode(targetMode);
        return;
      }

      // Update URL params and view mode together
      const newParams = new URLSearchParams(searchParams);
      if (section) {
        newParams.set("tab", "settings");
        newParams.set("section", section);
      } else {
        newParams.delete("tab");
        newParams.delete("section");
      }
      setSearchParams(newParams);
      state.setViewMode(targetMode);
    },
    [state.viewMode, history.length, searchParams, setSearchParams, state.setViewMode],
  );

  // Handler for rename success - update local project state only
  // Note: Don't call setCurrentProject here - RenameProjectModal already updates
  // the zustand store with all fields (including latestPublishUrl). Calling
  // setCurrentProject here would overwrite the store with stale data.
  const handleRenameSuccess = (newName: string, newUrlSlug: string) => {
    if (state.project) {
      state.setProject({
        ...state.project,
        name: newName,
        urlSlug: newUrlSlug,
      });
    }
  };

  // Handle removing a connection: unlink via API, refresh project, then send agent prompt to clean up code
  const handleRemoveConnection = useCallback(async (connection: Connection) => {
    try {
      const token = await getIdToken();
      if (!token) return;

      // 1. Unlink connection from project in the database
      await projectsApi.removeConnection(token, projectId, connection.id);

      // 2. Refresh project data so the popover updates
      await actions.loadProjectAndWorkspace();

      // 3. Send agent prompt to clean up code references
      const prompt = `The connection "${connection.name}" (ID: ${connection.id}, type: ${connection.type}) has been unlinked from this project. Clean up the project: remove any code referencing this connection's ID or its CONN_ environment variables, delete any associated edge functions, and clean up unused imports.`;
      state.setViewMode("agent");
      setTimeout(() => {
        agentSendPromptRef.current?.(prompt);
      }, 150);
    } catch (err) {
      console.error("[PowerEditor] Failed to remove connection:", err);
    }
  }, [getIdToken, projectId, actions.loadProjectAndWorkspace, state.setViewMode]);

  // Show status page for loading, error, or non-running states
  // Check error FIRST - error takes priority over loading state
  if (state.error && !state.workspace) {
    return (
      <PowerEditorStatusPage
        project={state.project}
        workspace={state.workspace}
        isResuming={state.isResuming}
        loading={state.loading}
        error={state.error}
        onStartWorkspace={actions.handleStartWorkspace}
        projectId={projectId}
      />
    );
  }

  if (state.loading && !state.workspace) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Workspace confirmed unreachable while DB says running (node failure):
  // every iframe/socket in the editor is dead, so a "normal" editor is a trap.
  // Show the recovery interstitial; polling in handleContainerUnhealthy exits
  // this state via force-pause auto-resume or reload-on-recovery.
  if (state.isWorkspaceUnreachable) {
    return (
      <PowerEditorStatusPage
        project={state.project}
        workspace={state.workspace}
        isResuming={state.isResuming}
        isUnreachable={true}
        loading={state.loading}
        error={state.error}
        onStartWorkspace={actions.handleStartWorkspace}
        projectId={projectId}
      />
    );
  }

  // Status page for non-running states (but NOT when resuming OR when we have a preview to show)
  // Show editor immediately if: resuming OR has preview URL (even if container not ready yet)
  const hasPreview =
    state.project?.latestPreviewBuildNumber || state.previewUrl;
  const shouldShowEditor = state.isResuming || hasPreview;

  if (
    state.workspace &&
    state.workspace.status !== "running" &&
    !shouldShowEditor
  ) {
    return (
      <PowerEditorStatusPage
        project={state.project}
        workspace={state.workspace}
        isResuming={state.isResuming}
        loading={state.loading}
        error={state.error}
        onStartWorkspace={actions.handleStartWorkspace}
        projectId={projectId}
      />
    );
  }

  // Running state - show integrated editor
  return (
    <div className="h-full flex flex-col">
      {/* Three step walkthrough, shown once on a user's first visit to an editor.
          Mounted here so it only exists where its anchors do. */}
      <EditorTour />

      {/* Header */}
      <div
        className={cn(
          "transition-all duration-300 ease-in-out overflow-visible relative z-50",
          state.isHeaderVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none absolute",
        )}
      >
        <PowerEditorHeader
          project={state.project}
          workspace={state.workspace}
          viewMode={state.viewMode}
          isSidebarHidden={state.isSidebarHidden}
          isResponsiveMode={state.isResponsiveMode}
          viewportSize={state.viewportSize}
          isCustomViewport={state.isCustomViewport}
          customWidth={state.customWidth}
          customHeight={state.customHeight}
          currentPath={state.currentPath}
          availablePaths={state.availablePaths}
          showDeviceDropdown={state.showDeviceDropdown}
          showPathDropdown={state.showPathDropdown}
          previewUrl={state.previewUrl}
          projectId={projectId}
          devContainerId={state.workspace?.id}
          previewIframeRef={state.previewIframeRef ?? undefined}
          isAgentStreaming={state.isAgentStreaming}
          onExpandPreview={actions.handleExpandPreview}
          onToggleDeviceDropdown={() =>
            state.setShowDeviceDropdown(!state.showDeviceDropdown)
          }
          onTogglePathDropdown={actions.handleTogglePathDropdown}
          onPathSelect={handlePathSelectWithGuard}
          onViewportChange={state.setViewportSize}
          onCustomViewportChange={(width, height) => {
            state.setCustomWidth(width);
            state.setCustomHeight(height);
            state.setIsCustomViewport(true);
          }}
          onResponsiveModeChange={state.setIsResponsiveMode}
          onReloadPreview={actions.handleReloadPreview}
          onOpenPreviewInNewWindow={actions.handleOpenPreviewInNewWindow}
          onSettingsClick={handleSettingsNavigation}
          onMenuItemClick={setActiveMenuItem}
          onViewModeChange={handleViewModeChangeWithGuard}
          onRefreshProject={actions.loadProjectAndWorkspace}
          onRenameClick={() => setShowRenameModal(true)}
          onRemoveConnection={handleRemoveConnection}
          mobileActiveTab={state.mobileActiveTab}
          showMobileTabSwitcher={state.showMobileTabSwitcher}
          onToggleMobileTabSwitcher={() =>
            state.setShowMobileTabSwitcher(!state.showMobileTabSwitcher)
          }
          onMobileOpenSettings={(section) => {
            if (mobileSettingsOpenerRef.current) {
              mobileSettingsOpenerRef.current(section);
            }
          }}
        />
      </div>

      {/* Integrated Editor with Agent */}
      <div className="flex-1 min-h-0">
        {/* Show editor immediately if: have accessUrl (normal) OR in dev OR have preview (instant resume) */}
        {state.workspace &&
        (state.accessUrl || state.isDevelopment || hasPreview) ? (
          <IntegratedEditorWithAgent
            workspaceUrl={state.computedIdeUrl || ""}
            actualWorkspaceUrl={
              state.accessUrl ||
              `https://${state.workspace.subdomain}.dev-sandbox2.optidev.com`
            }
            devContainerId={state.workspace.id}
            projectId={projectId}
            previewUrl={state.computedPreviewUrl}
            workspaceId={state.project?.workspaceId || ""}
            projectName={state.project?.name || ""}
            isLoading={false}
            isContainerReady={
              state.workspace.status === "running" && !!state.computedIdeUrl
            }
            isPreviewLoading={state.isPreviewLoading}
            isShowingStaticPreview={state.isShowingStaticPreview}
            isDevelopment={state.isDevelopment}
            previewPort={
              state.isDevelopment
                ? import.meta.env.VITE_PREVIEW_PORT || "3005"
                : state.appService?.port?.toString() || "5173"
            }
            viewMode={state.viewMode}
            onViewModeChange={handleViewModeChangeWithGuard}
            initialSection={searchParams.get("section") || undefined}
            onSettingsSectionChange={(section) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set("tab", "settings");
              newParams.set("section", section);
              setSearchParams(newParams);
            }}
            onReloadPreview={actions.handleReloadPreview}
            onOpenPreviewInNewWindow={actions.handleOpenPreviewInNewWindow}
            onExpandPreview={actions.handleExpandPreview}
            isSidebarHidden={state.isSidebarHidden}
            isFullscreenChat={state.isFullscreenChat}
            onFullscreenChatChange={state.setIsFullscreenChat}
            showPreviewInAgentView={state.showPreview}
            onShowPreviewInAgentViewChange={state.setShowPreview}
            onVisualEditorRef={(ref) => {
              visualEditorRef.current = ref.current;
            }}
            onPreviewIframeRef={(ref) => {
              state.setPreviewIframeRef(ref);
              if (ref) {
                state.setPreviewIframeForPollingRef(ref.current);
              }
            }}
            onPreviewLoaded={actions.handlePreviewLoaded}
            onCommitStateChange={state.setCommitState}
            isResponsiveMode={state.isResponsiveMode}
            onResponsiveModeChange={state.setIsResponsiveMode}
            viewportSize={state.viewportSize}
            isCustomViewport={state.isCustomViewport}
            customWidth={state.customWidth}
            customHeight={state.customHeight}
            onViewportResize={actions.handleViewportResize}
            onPathChange={actions.handlePathChange}
            currentPath={state.currentPath}
            availablePaths={state.availablePaths}
            showDeviceDropdown={state.showDeviceDropdown}
            showPathDropdown={state.showPathDropdown}
            onPathSelect={handlePathSelectWithGuard}
            onToggleDeviceDropdown={() =>
              state.setShowDeviceDropdown(!state.showDeviceDropdown)
            }
            onTogglePathDropdown={actions.handleTogglePathDropdown}
            fromApp={state.fromApp}
            builderData={state.builderData}
            onBuilderDataProcessed={state.handleBuilderDataProcessed}
            onAgentStreamingChange={state.setIsAgentStreaming}
            activeMenuItem={activeMenuItem}
            onActiveMenuItemChange={setActiveMenuItem}
            showMobileTabSwitcher={state.showMobileTabSwitcher}
            onMobileActiveTabChange={(tab) => state.setMobileActiveTab(tab)}
            onHeaderVisibilityChange={state.setIsHeaderVisible}
            onStreamComplete={actions.fetchRoutes}
            onConnectionLinked={actions.loadProjectAndWorkspace}
            onAgentSendPrompt={(sendFn) => { agentSendPromptRef.current = sendFn; }}
            onMobileSettingsOpener={(openFn) => { mobileSettingsOpenerRef.current = openFn; }}
            sessionHeaderLeftActions={
              !isMobile ? (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => handleViewModeChangeWithGuard("agent")}
                    className={cn(
                      "px-2 py-1 text-xs font-medium rounded-md transition-colors",
                      state.viewMode === "agent"
                        ? "text-white bg-purple-600"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Chat
                  </button>
                  <button
                    disabled={
                      state.isAgentStreaming ||
                      state.isShowingStaticPreview
                    }
                    onClick={() => {
                      if (
                        state.isAgentStreaming ||
                        state.isShowingStaticPreview
                      )
                        return;
                      handleViewModeChangeWithGuard("visual");
                      state.setShowPreview(true);
                    }}
                    className={cn(
                      "px-2 py-1 text-xs font-medium rounded-md transition-colors",
                      state.viewMode === "visual"
                        ? "text-white bg-purple-600"
                        : "text-muted-foreground hover:text-foreground",
                      (state.isAgentStreaming || state.isShowingStaticPreview) &&
                        "opacity-40 cursor-not-allowed",
                    )}
                  >
                    Visual
                  </button>
                </div>
              ) : undefined
            }
            sessionHeaderRightActions={
              !isMobile ? (
                <div className="flex items-center gap-1">
                  {state.viewMode === "visual" && (
                    <Hint
                      delayDuration={0}
                      text="Image Generator"
                      side="bottom"
                      align="center"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => state.setViewMode("design")}
                        className={cn(
                          POWER_EDITOR_STYLES.buttonBase,
                          "hover:bg-muted",
                        )}
                      >
                        <ImagePlus className="w-4 h-4" />
                      </Button>
                    </Hint>
                  )}
                  {state.viewMode === "visual" && features.mediaLibrary && (
                    <Hint
                      delayDuration={0}
                      text="Media Library"
                      side="bottom"
                      align="center"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => state.setViewMode("media")}
                        className={cn(
                          POWER_EDITOR_STYLES.buttonBase,
                          "hover:bg-muted",
                        )}
                      >
                        <Images className="w-4 h-4" />
                      </Button>
                    </Hint>
                  )}
                  {state.viewMode === "visual" && (
                    <Hint
                      delayDuration={0}
                      text="Themes"
                      side="bottom"
                      align="center"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setActiveMenuItem(
                            activeMenuItem === "themes" ? null : "themes"
                          )
                        }
                        className={cn(
                          POWER_EDITOR_STYLES.buttonBase,
                          "hover:bg-muted",
                          activeMenuItem === "themes" && "bg-muted",
                        )}
                      >
                        <Palette className="w-4 h-4" />
                      </Button>
                    </Hint>
                  )}
                  {(state.viewMode === "agent" ||
                    state.viewMode === "history" ||
                    state.viewMode === "design") && (
                    <Hint
                      delayDuration={0}
                      text="Version History"
                      side="bottom"
                      align="center"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          state.setViewMode(
                            state.viewMode === "history"
                              ? "agent"
                              : "history",
                          )
                        }
                        className={cn(
                          POWER_EDITOR_STYLES.buttonBase,
                          "hover:bg-muted",
                          state.viewMode === "history" && "bg-muted",
                        )}
                      >
                        <Clock className="w-4 h-4" />
                      </Button>
                    </Hint>
                  )}
                  {(state.viewMode === "agent" ||
                    state.viewMode === "history" ||
                    state.viewMode === "design") && (
                    <Hint
                      delayDuration={0}
                      text={
                        state.showPreview
                          ? "Show Code Editor"
                          : "Show Preview"
                      }
                      side="bottom"
                      align="center"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          state.setShowPreview(!state.showPreview)
                        }
                        disabled={state.isPreviewDisabled}
                        className={cn(
                          POWER_EDITOR_STYLES.buttonBase,
                          "hover:bg-muted",
                        )}
                      >
                        {state.showPreview ? (
                          <Code className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </Hint>
                  )}
                </div>
              ) : undefined
            }
            visualEditorFloatingControls={
              <PowerEditorFloatingControls
                pendingChangesCount={
                  getAllPendingChanges(history, currentIndex).size
                }
                canUndo={canUndoValue}
                canRedo={canRedoValue}
                isSavingChanges={state.isSavingChanges}
                commitState={state.commitState}
                onUndo={actions.handleUndo}
                onRedo={actions.handleRedo}
                onSaveChanges={actions.handleSaveChanges}
                onDiscardChanges={actions.handleDiscardChanges}
              />
            }
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-background">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-500" />
              <p className="text-muted-foreground">
                Connecting to development environment...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Rename Project Modal */}
      {state.project && (
        <RenameProjectModal
          open={showRenameModal}
          onOpenChange={setShowRenameModal}
          projectId={projectId}
          currentName={state.project.name}
          onSuccess={handleRenameSuccess}
        />
      )}

      {/* Unsaved visual editor changes dialog (path navigation or view mode switch) */}
      <UnsavedChangesDialog
        open={!!pendingPathRoute || !!pendingViewMode}
        onCancel={() => {
          setPendingPathRoute(null);
          setPendingViewMode(null);
        }}
        onDiscard={() => {
          actions.handleDiscardChanges();
          if (pendingPathRoute) {
            actions.handlePathSelect(pendingPathRoute);
          }
          if (pendingViewMode) {
            state.setViewMode(pendingViewMode);
          }
          setPendingPathRoute(null);
          setPendingViewMode(null);
        }}
        description={`You have unsaved changes in the visual editor. Are you sure you want to ${pendingPathRoute ? "navigate to a different page" : "switch tabs"} without saving?`}
        actionLabel="Discard & Continue"
      />
    </div>
  );
}
